import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ChevronLeft, TrendingUp, TrendingDown, Users, FileCheck, CreditCard, BarChart3, AlertTriangle, ArrowRight, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import apiService from '@/services/apiService';

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getAnalyticsDashboard(period);
      setData(result);
    } catch (err) {
      setError('Failed to load analytics data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getDropOffColor = (rate) => {
    if (rate >= 50) return 'text-destructive';
    if (rate >= 30) return 'text-warning';
    return 'text-verify';
  };

  const formatNumber = (num) => {
    if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
    return num?.toString() || '0';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-600 hover:text-trust transition-colors"
            data-testid="back-button"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-public">Back</span>
          </button>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-trust" />
            <span className="font-outfit font-bold text-xl text-slate-900">Analytics</span>
          </div>
          <button
            onClick={loadAnalytics}
            className="flex items-center gap-2 text-slate-600 hover:text-trust transition-colors"
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Period Selector */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-outfit font-bold text-2xl text-slate-900">
            Conversion Funnel & Drop-off Analysis
          </h1>
          <div className="flex gap-2">
            {[7, 14, 30, 90].map((days) => (
              <button
                key={days}
                onClick={() => setPeriod(days)}
                className={`px-4 py-2 rounded-md font-public text-sm transition-colors ${
                  period === days
                    ? 'bg-trust text-trust-foreground'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`period-${days}`}
              >
                {days}d
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-8">
            <p className="text-destructive">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-trust animate-spin" />
          </div>
        ) : data ? (
          <div className="space-y-8">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-trust/10 p-2 rounded-lg">
                    <Users className="w-5 h-5 text-trust" />
                  </div>
                  <span className="font-public text-sm text-slate-500">Total Sessions</span>
                </div>
                <p className="font-outfit font-bold text-3xl text-slate-900">
                  {formatNumber(data.total_sessions)}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-verify/10 p-2 rounded-lg">
                    <CreditCard className="w-5 h-5 text-verify" />
                  </div>
                  <span className="font-public text-sm text-slate-500">Conversions</span>
                </div>
                <p className="font-outfit font-bold text-3xl text-slate-900">
                  {formatNumber(data.total_conversions)}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="font-public text-sm text-slate-500">Conversion Rate</span>
                </div>
                <p className="font-outfit font-bold text-3xl text-slate-900">
                  {data.overall_conversion_rate}%
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-warning/10 p-2 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-warning" />
                  </div>
                  <span className="font-public text-sm text-slate-500">Biggest Drop-off</span>
                </div>
                <p className="font-outfit font-bold text-lg text-slate-900 truncate">
                  {data.highest_drop_off?.label || 'N/A'}
                </p>
                <p className="text-sm text-slate-500">
                  {data.highest_drop_off?.abandoned_sessions || 0} sessions
                </p>
              </motion.div>
            </div>

            {/* Funnel Visualization */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-6">
                <BarChart3 className="w-5 h-5 text-trust" />
                <h2 className="font-outfit font-bold text-lg text-slate-900">Conversion Funnel</h2>
              </div>
              
              <div className="space-y-4">
                {data.funnel?.map((stage, index) => {
                  const maxCount = data.funnel[0]?.count || 1;
                  const widthPercent = (stage.count / maxCount) * 100;
                  
                  return (
                    <div key={stage.stage} className="relative">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-public text-sm text-slate-700">{stage.label}</span>
                        <div className="flex items-center gap-4">
                          <span className="font-outfit font-semibold text-slate-900">{stage.count}</span>
                          {index > 0 && stage.drop_off_rate > 0 && (
                            <span className={`text-sm flex items-center gap-1 ${getDropOffColor(stage.drop_off_rate)}`}>
                              <TrendingDown className="w-3 h-3" />
                              {stage.drop_off_rate}%
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="h-8 bg-slate-100 rounded-md overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${widthPercent}%` }}
                          transition={{ duration: 0.8, delay: index * 0.1 }}
                          className={`h-full rounded-md ${
                            index === data.funnel.length - 1 ? 'bg-verify' : 'bg-trust'
                          }`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            {/* Drop-off Analysis & Visa Types */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Drop-off Points */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm"
              >
                <div className="flex items-center gap-3 mb-6">
                  <AlertTriangle className="w-5 h-5 text-warning" />
                  <h2 className="font-outfit font-bold text-lg text-slate-900">Where Users Abandon</h2>
                </div>
                
                {data.drop_off_points?.length > 0 ? (
                  <div className="space-y-3">
                    {data.drop_off_points.slice(0, 5).map((point, index) => (
                      <div key={point.stage} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                        <div className="flex items-center gap-3">
                          <span className="text-slate-400 font-mono text-sm">#{index + 1}</span>
                          <span className="font-public text-slate-700">{point.label}</span>
                        </div>
                        <span className="font-outfit font-semibold text-slate-900">
                          {point.abandoned_sessions} sessions
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">No drop-off data yet</p>
                )}
              </motion.div>

              {/* Visa Type Breakdown */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm"
              >
                <div className="flex items-center gap-3 mb-6">
                  <FileCheck className="w-5 h-5 text-verify" />
                  <h2 className="font-outfit font-bold text-lg text-slate-900">Popular Visa Types</h2>
                </div>
                
                {data.visa_type_breakdown?.length > 0 ? (
                  <div className="space-y-3">
                    {data.visa_type_breakdown.slice(0, 5).map((visa, index) => (
                      <div key={visa.visa_type} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                        <div className="flex items-center gap-3">
                          <span className="text-slate-400 font-mono text-sm">#{index + 1}</span>
                          <span className="font-public text-slate-700 truncate max-w-[200px]">
                            {visa.visa_type}
                          </span>
                        </div>
                        <span className="font-outfit font-semibold text-slate-900">
                          {visa.selections}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">No visa selection data yet</p>
                )}
              </motion.div>
            </div>

            {/* Action Items */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-trust/5 border border-trust/20 rounded-lg p-6"
            >
              <h2 className="font-outfit font-bold text-lg text-slate-900 mb-4">
                Insights & Recommendations
              </h2>
              
              <ul className="space-y-3">
                {data.highest_drop_off && (
                  <li className="flex items-start gap-3">
                    <ArrowRight className="w-5 h-5 text-trust flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-public text-slate-700">
                        <span className="font-semibold">{data.highest_drop_off.abandoned_sessions} users</span> abandon at 
                        <span className="font-semibold"> {data.highest_drop_off.label}</span>.
                      </p>
                      <p className="text-sm text-slate-500">
                        Consider simplifying this step or adding progress indicators.
                      </p>
                    </div>
                  </li>
                )}
                
                {data.overall_conversion_rate < 5 && (
                  <li className="flex items-start gap-3">
                    <ArrowRight className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-public text-slate-700">
                        Conversion rate is below 5%. Consider:
                      </p>
                      <ul className="text-sm text-slate-500 ml-4 mt-1 list-disc">
                        <li>Adding trust signals and testimonials</li>
                        <li>Reducing friction in the payment flow</li>
                        <li>Offering a free preview before payment</li>
                      </ul>
                    </div>
                  </li>
                )}
                
                {data.visa_type_breakdown?.length > 0 && (
                  <li className="flex items-start gap-3">
                    <ArrowRight className="w-5 h-5 text-verify flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-public text-slate-700">
                        Most popular visa type: <span className="font-semibold">{data.visa_type_breakdown[0]?.visa_type}</span>
                      </p>
                      <p className="text-sm text-slate-500">
                        Consider featuring this on the landing page for quick access.
                      </p>
                    </div>
                  </li>
                )}
              </ul>
            </motion.div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
